import type { PropsWithChildren } from 'react';
import { Column, Row } from './box';
import { Text } from './text';
import type { ColorValue, ViewStyle } from 'react-native';
import { Pressable } from 'react-native';
import type { StorageKey } from 'app/lib/storage';
import { useStoredValue } from 'app/lib/storage';
import { Event, track } from 'app/lib/analytics';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { getAlertHideKey } from './alert.utils';

export { isAlertHidden } from './alert.utils';

const theme = createDSTheme();

interface AlertProps {
  title: string;
  color?: ColorValue;
  backgroundColor?: ColorValue;
  hideKey?: string;
  onHide?: () => void;
  style?: ViewStyle;
}

export function Alert({
  children,
  title,
  color = theme.colors['surface.error.default'],
  backgroundColor = theme.colors['surface.brand.secondary-4.subtle'],
  hideKey,
  onHide,
  style,
}: PropsWithChildren<AlertProps>) {
  const [isHidden, setIsHidden] = useStoredValue(getAlertHideKey(hideKey ?? '') as StorageKey, false);

  const doHide = () => {
    setIsHidden(true);
    track(Event.ClosedAlert, { key: hideKey! });
    onHide?.();
  };

  if (hideKey && isHidden) {
    return null;
  }

  return (
    <Column borderRadius="radius.lg" padding="spacing.general.x4" style={[{ backgroundColor }, style]} width="100%">
      <Row justifyContent="space-between" marginBottom="spacing.general.x4">
        <Row alignItems="center">
          <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={color} size={24} />
          <Text fontWeight="700" marginStart="spacing.general.x1" style={{ color }} variant="bodyBase">
            {title}
          </Text>
        </Row>

        {hideKey ? (
          <Pressable onPress={doHide}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={color} />
          </Pressable>
        ) : null}
      </Row>

      {children}
    </Column>
  );
}
